import React, { useRef, useState } from 'react';
import PaginatedTable, { usePaginatedTable } from '@/components/common/DataTable/PaginatedTable';
import { matchAttributesForSingleRow } from '@/components/common/DataTable/Utils';
import AdvancedFilters from '@/components/common/AdvancedFilters';
import { buildFilterPath } from '@/components/common/AdvancedFilters/Utils';
import { ReactQueryProvider } from '@/utils/ReactQuery';
import {
  IMAGES_PATH,
  MAX_RECORDS,
  SEARCH_IMAGES_PATH,
  PAGE_SIZE,
  IMAGE_FILTERS,
  getInitialImageFilters,
} from './constants';
import EditImageModal, { useEditImageModal } from './EditImageModal';
import useImageTableColumns from './useImageTableColumns';

export default function ImagesTable() {
  const [filterSent, setFilterSent] = useState(false);
  const [getUrl, setGetUrl] = useState(IMAGES_PATH);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const loadedCountRef = useRef(0);

  const { openEditImageModal, ...EditImageModalProps } = useEditImageModal();

  const { columns, columnMapping, columnDefs } = useImageTableColumns({ onEditAction: openEditImageModal });

  const {
    resources,
    pagesLoaded,
    addResources,
    doneLoading,
    setDoneLoading,
    resetResources,
    updateResource,
    setPagesLoaded,
  } = usePaginatedTable(PAGE_SIZE);

  const _addResources = (_resources, meta) => {
    if (meta?.count != null) setTotalCount(meta.count);

    // AIDEV-NOTE: filterSent=true means a new filter was just applied. We discard the first response
    // (which was in-flight from before the filter change) and let PaginatedTable fire a fresh request.
    // This causes a double-fetch on every filter change but prevents stale data from appearing.
    if (filterSent) {
      loadedCountRef.current = 0;
      resetResources();
      setFilterSent(false);
      return;
    }

    const remaining = MAX_RECORDS - loadedCountRef.current;
    const trimmed = remaining < _resources.length ? _resources.slice(0, remaining) : _resources;

    loadedCountRef.current += trimmed.length;
    addResources(trimmed);
  };

  return (
    <ReactQueryProvider>
      <div>
        <AdvancedFilters
          initialFilters={getInitialImageFilters()}
          properties={IMAGE_FILTERS}
          searchPath={getUrl}
          resources={resources}
          updateActiveFilters={(newFilters) => {
            setGetUrl(buildFilterPath(IMAGES_PATH, newFilters));
            setDoneLoading(false);
            resetResources();
            setPagesLoaded(0);
            setFilterSent(true);
            loadedCountRef.current = 0;
          }}
          asyncSearchPath={SEARCH_IMAGES_PATH}
        />
        <hr />
      </div>

      <p className={`tw-text-sm tw-text-gray-500 tw-mb-2 tw-text-right ${doneLoading && totalCount != null && totalCount > MAX_RECORDS ? 'tw-visible' : 'tw-invisible'}`}>
        Showing {MAX_RECORDS.toLocaleString()} of {(totalCount ?? 0).toLocaleString()} images. Use filters to narrow results.
      </p>

      <PaginatedTable
        autoWidth={false}
        columns={columns}
        columnDefs={columnDefs}
        columnMapping={columnMapping}
        exportedColumns={[1, 2, 3, 4, 5, 6]}
        defaultOrder={[[1, 'asc'], [4, 'asc']]}
        getUrl={getUrl}
        pageSize={PAGE_SIZE}
        pagesLoaded={pagesLoaded}
        doneLoading={doneLoading}
        resources={resources}
        addResources={_addResources}
        resetResources={resetResources}
      />

      {EditImageModalProps.image && (
        <EditImageModal
          {...EditImageModalProps}
          updateRow={(rowData) => {
            matchAttributesForSingleRow(rowData, columns);
            updateResource(rowData);
          }}
        />
      )}
    </ReactQueryProvider>
  );
}
